import { legacyProductKey, type Marketplace } from "./listing-key";

export interface StockSource {
  present: number;
  reserved: number;
  source: string;
}

export interface Turnover {
  ads: number;
  idc: number;
  turnover: number;
  idc_grade: string;
  turnover_grade: string;
}

export interface Warehouse {
  name: string;
  warehouse_id: string;
  cluster: string;
  cluster_id: string;
  free: number;
  valid: number;
  reserved: number;
  in_transit: number;
  days_without_sales: number;
  excess: number;
  expiring: number;
  defect: number;
  transit_defect: number;
  return_customer: number;
  return_seller: number;
  waiting_docs: number;
  requested: number;
  other: number;
  tags: string[];
  turnover_grade: string;
  ads: number;
}

export interface Analytics {
  revenue: number;
  ordered_units: number;
  returns: number;
  cancellations: number;
  delivered_units: number;
  position_category: number;
  hits_view: number;
  hits_tocart: number;
  hits_tocart_search: number;
  hits_tocart_pdp: number;
  hits_view_search: number;
  hits_view_pdp: number;
  conv_tocart: number;
  conv_tocart_search: number;
  conv_tocart_pdp: number;
  session_view: number;
  session_view_search: number;
  session_view_pdp: number;
  /** Per-day breakdown за последние 30 дней. Каждый массив длины 30,
   *  индекс 0 = 30 дней назад, индекс 29 = вчера.
   *  Опциональное — у магазинов до фичи или с deprecated метриками — undefined.
   *  Заполняется stepAnalytics через `dimension: ["day","sku"]`.
   *  Используется для графиков «как менялись метрики» и AI-детекта аномалий. */
  daily?: Partial<Record<
    | "revenue" | "ordered_units" | "returns" | "cancellations" | "delivered_units"
    | "hits_view" | "hits_view_search" | "hits_view_pdp"
    | "hits_tocart" | "hits_tocart_search" | "hits_tocart_pdp"
    | "conv_tocart" | "conv_tocart_search" | "conv_tocart_pdp"
    | "session_view" | "session_view_search" | "session_view_pdp"
    | "position_category",
    number[]
  >>;
}

/**
 * Lookup-ключ для per-offer данных (orders/prices/performance/...).
 * Значение приходит с бэка в поле `__key` — оно всегда правильное для текущего
 * режима (single-store: offer_id, multi-store: storeId:offer_id). Фронт сам
 * режим не разбирает.
 *
 * Fallback на старую логику нужен только для продуктов из мест где __key ещё
 * не проставлен (например, старый кэш браузера до рефакторинга 2026-04-21).
 */
export function dataKey(p: { offer_id: string; __key?: string; __store?: { id: string; name: string } }): string {
  if (p.__key) return p.__key;
  return legacyProductKey(p.offer_id, p.__store?.id);
}

/**
 * Lookup-ключ для performance/ads данных. Performance API хранит offer_id в
 * UPPERCASE (см. lib/refresh/steps/performance.ts), поэтому для adData ключ
 * другой — offer_id принудительно в верхний регистр. Префикс магазина остаётся
 * как есть (lowercase st_XXX), иначе не матчит ключ из readCacheRecordMerged.
 */
export function adKey(p: { offer_id: string; __store?: { id: string; name: string } }): string {
  const offer = (p.offer_id ?? "").toUpperCase();
  return p.__store ? `${p.__store.id}:${offer}` : offer;
}

export interface Product {
  id: number;
  sku?: number;
  name: string;
  offer_id: string;
  price: string;
  old_price: string;
  primary_image: string[];
  images: string[];
  stocks: { has_stock: boolean; stocks: StockSource[] };
  statuses: { status_name: string; moderate_status: string };
  is_archived: boolean;
  weight?: number;
  depth?: number;
  height?: number;
  width?: number;
  dimension_unit?: string;
  weight_unit?: string;
  vat?: string;
  service_type?: string;
  is_kgt?: boolean;
  is_prepayment_allowed?: boolean;
  has_discounted_fbo_item?: boolean;
  is_discounted?: boolean;
  is_super?: boolean;
  description_category_id?: number;
  category_name?: string;
  type_id?: number;
  type_name?: string;
  barcodes?: string[];
  sources?: { sku: number; source: string; shipment_type?: string }[];
  model_info?: { model_id?: number | string; count?: number };
  availabilities?: { availability: string; sku: number; source: string; reasons?: unknown[] }[];
  errors?: { code: string; field?: string; description?: string }[];
  analytics: Analytics | null;
  turnover: Turnover | null;
  warehouses: Warehouse[];
  /** Маркетплейс листинга. Пока все текущие товары приходят из Ozon; поле
   *  добавлено как подготовка к WB и другим источникам. */
  marketplace?: Marketplace;
  /** Универсальный id карточки внутри маркетплейса. Для Ozon = offer_id. */
  listing_id?: string;
  /** Канонический ключ будущей multi-marketplace модели:
   *  `${marketplace}:${storeId}:${listing_id}`. Старый `__key` не заменяет. */
  listing_key?: string;
  /** В режиме «Все магазины» бэк добавляет к каждому товару метку откуда он
   *  пришёл — для UI-бейджа в строке и в дашборде. В single-store режиме
   *  отсутствует. */
  __store?: { id: string; name: string };
  /** Унифицированный lookup-ключ: в single-store = offer_id, в multi-store =
   *  `${storeId}:${offer_id}`. Проставляется сервером в lib/store-query.ts.
   *  Фронт использует через `dataKey(p)` — не собирает ключ сам. */
  __key?: string;
  /** Лёгкие поля для группировки view-таблиц без ожидания полного attributes. */
  group_article?: string;
  group_color_key?: string;
  group_color_label?: string;
  group_size_value?: string;
  group_size_sort?: number;
}

export type AdStats = {
  spend: number; views: number; clicks: number;
  orders: number; revenue: number; ctr: number; cpc: number; drr: number;
  avgBid?: number; createdAt?: string;
  modelOrders?: number; modelRevenue?: number; productGmv?: number;
  generalDrr?: number; toCart?: number;
};

export type OrderStats = {
  daily_12d: number[];
  daily_30d: number[];         // non-cancelled заказы по in_process_at для реконструкции остатков
  daily_orders_30d: number[];  // все заказы (с отменёнными) по created_at за 30 дней, index 0 = 30 дней назад
  daily_revenue_30d: number[]; // выплата (payout) по created_at за 30 дней, index 0 = 30 дней назад
  daily_price_30d:   number[]; // сумма цен заказов по created_at за 30 дней (÷ daily_orders_30d = средняя цена)
  daily_price_rub_customer_30d?: number[]; // сумма цен заказов только для позиций, где покупатель платил в RUB
  daily_clusters_to_30d?: Record<string, number>[];   // куда заказали по дням, index 0 = 30 дней назад
  daily_clusters_from_30d?: Record<string, number>[]; // откуда отгрузили по дням, index 0 = 30 дней назад
  daily_customer_price_30d?: number[];       // сумма customer_price по created_at за 30 дней
  daily_customer_price_count_30d?: number[]; // количество единиц с customer_price по created_at
  daily_customer_price_rub_30d?: number[];       // сумма customer_price только в RUB
  daily_customer_price_rub_count_30d?: number[]; // количество единиц с customer_price в RUB
  qty_1d: number; qty_7d: number; qty_14d?: number; qty_30d: number; qty_month: number; qty_in_transit: number;
  qty_prev_7d?: number; qty_prev_14d?: number; qty_prev_30d?: number;
  revenue_30d: number; commission_30d: number;
  clusters_to: Record<string, number>;            // 30 полных МСК-дней (back-compat)
  clusters_to_1d?: Record<string, number>;         // куда заказали вчера — по created_at, с отменами
  clusters_to_7d?: Record<string, number>;         // 7 дней — для скорости per-cluster
  clusters_to_14d?: Record<string, number>;        // 14 дней — для скорости per-cluster
  clusters_to_price_7d?: Record<string, number>;   // сумма цен заказов по кластерам за 7 дней
  clusters_to_price_rub_customer_7d?: Record<string, number>; // сумма цен заказов по кластерам только там, где customer_price в RUB
  clusters_to_customer_price_7d?: Record<string, number>; // сумма оплаты покупателем по кластерам за 7 дней
  clusters_to_customer_price_count_7d?: Record<string, number>; // количество единиц с customer_price по кластерам за 7 дней
  clusters_to_customer_price_by_currency_7d?: Record<string, Record<string, { value: number; count: number }>>;
  clusters_to_prev_7d?: Record<string, number>;
  clusters_to_prev_14d?: Record<string, number>;
  clusters_to_prev_30d?: Record<string, number>;
  clusters_from: Record<string, number>;           // 30 дней — отгрузки из кластера по in_process_at, без отмен
  clusters_from_7d?: Record<string, number>;        // 7 дней — отгрузки из кластера по in_process_at, без отмен
  clusters_from_14d?: Record<string, number>;       // 14 дней — отгрузки из кластера по in_process_at, без отмен
  clusters_from_prev_7d?: Record<string, number>;
  clusters_from_prev_14d?: Record<string, number>;
  clusters_from_prev_30d?: Record<string, number>;
  clusters_to_from?: Record<string, Record<string, number>>; // куда заказали → откуда отгрузили
  clusters_to_from_7d?: Record<string, Record<string, number>>;
  clusters_to_from_14d?: Record<string, Record<string, number>>;
  clusters_from_to?: Record<string, Record<string, number>>; // откуда отгрузили → куда заказали
  clusters_from_to_7d?: Record<string, Record<string, number>>;
  clusters_from_to_14d?: Record<string, Record<string, number>>;
  warehouses: Record<string, number>;
};

export type PriceInfo = {
  min_price: number | null;
  marketing_price: number | null;
  marketing_seller_price: number | null;
  net_price: number | null;
  retail_price: number | null;
  commission_fbo: number | null;
  commission_fbs: number | null;
  color_index: string | null;
  ozon_index_value: number | null;
  ozon_min_competitor: number | null;
  ext_index_value: number | null;
  ext_min_price: number | null;
  volume_weight: number | null;
  has_promotion: boolean;
  promo_from: string | null;
  promo_to: string | null;
};

/**
 * Главный поисковый запрос товара. Частотность (`searches`) — сколько человек
 * искали эту фразу на Ozon за период, а не сколько раз показали объявление:
 * данные органические. Период обязателен рядом со значением — окно Ozon
 * скользящее, и без подписи число выглядело бы вечным.
 */
export type TopQueryInfo = {
  query: string;
  searches: number;
  position: number | null;
  dateFrom: string;
  dateTo: string;
};

export function getStocks(p: Product) {
  const list = p.stocks?.stocks ?? [];
  const present = list.reduce((s, x) => s + (x.present ?? 0), 0);
  const reserved = list.reduce((s, x) => s + (x.reserved ?? 0), 0);
  return { present, reserved };
}

/**
 * Заказы за 14 дней.
 *
 * Ozon такого числа не отдаёт: раньше поле qty_14d писалось нулём, и три места
 * считали его по-разному — ЯЧЕЙКА складывала дневной ряд и показывала верно, а
 * СОРТИРОВКА и ИТОГИ читали пустое поле и получали ноль. Из-за этого столбец
 * выглядел правильным, но сортировался и суммировался как нулевой.
 *
 * Одна точка правды: берём сохранённое значение, а если его нет (или оно ноль
 * от старой записи) — складываем последние 14 дней дневного ряда. Настоящий
 * ноль от этого не пострадает: сумма пустых дней тоже ноль.
 */
export function orderQuantity14d(stats: { qty_14d?: number | null; daily_orders_30d?: number[] } | undefined | null): number {
  const stored = stats?.qty_14d;
  if (stored) return stored;
  return (stats?.daily_orders_30d ?? []).slice(-14).reduce((sum, value) => sum + (value || 0), 0);
}
